import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, Typography, Paper, Fade } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconSeeker } from '@ui-seeker/core'
import { dsColorNew } from '@palette'
import { TOUR_STEPS } from './steps'


const CURSOR_MS = 400
const TOOLTIP_DELAY_MS = 650

const BRAND_GRADIENT = `linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan})`
const BRAND_GRADIENT_HOVER = `linear-gradient(135deg, ${dsColorNew.webBlueHover}, ${dsColorNew.cyanHover})`
const BACKDROP = alpha(dsColorNew.navy, 0.62)


const CARD_WIDTH = 370

function getTooltipPosition(rect, th = 300) {
	if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

	const pad = 20
	const tw = CARD_WIDTH
	const vw = window.innerWidth
	const vh = window.innerHeight

	if (rect.right + pad + tw < vw) {
		return { top: Math.max(pad, Math.min(rect.top, vh - th - pad)), left: rect.right + pad }
	}
	if (rect.left - pad - tw > 0) {
		return { top: Math.max(pad, Math.min(rect.top, vh - th - pad)), left: rect.left - pad - tw }
	}
	if (rect.bottom + pad + th < vh) {
		return { top: rect.bottom + pad, left: Math.max(pad, Math.min(rect.left, vw - tw - pad)) }
	}
	if (rect.top - pad - th > 0) {
		return { top: rect.top - th - pad, left: Math.max(pad, Math.min(rect.left, vw - tw - pad)) }
	}
	// No side has room (a target taller than the screen allows): the card at the bottom and the spotlight
	// stops above it (clipBottom), so the card never covers what it talks about
	return { top: vh - th - pad, left: Math.max(pad, Math.min(rect.left, vw - tw - pad)), clipBottom: vh - th - pad - 14 }
}


function CursorSVG() {
	return <svg width="28" height="32" viewBox="0 0 16 19" style={{ filter: `drop-shadow(2px 3px 4px ${alpha(dsColorNew.navy, 0.45)})` }}>
		<path
			d="M1.5 1 L1.5 15.5 L5 11.5 L8 17.5 L10 16.5 L7 10.5 L11.5 10.5 Z"
			fill={dsColorNew.white} stroke={dsColorNew.navy} strokeWidth="0.9" strokeLinejoin="round"
		/>
	</svg>
}


export function TourOverlay({ onClose, steps: externalSteps, modalRanges = [] }) {
	const tourSteps = externalSteps || TOUR_STEPS
	const [step, setStep] = useState(0)
	const [targetRect, setTargetRect] = useState(null)
	const [showTooltip, setShowTooltip] = useState(false)
	const [isClicking, setIsClicking] = useState(false)
	const [waiting, setWaiting] = useState(false)
	const pollRef = useRef(null)
	const cardRef = useRef(null)
	const [cardHeight, setCardHeight] = useState(undefined)

	const current = tourSteps[step]
	const isField = current.type === 'field'
	const totalSteps = tourSteps.length

	// displayedStep tracks which step's content is actually visible in the tooltip.
	// It updates only after the fade-out completes (300ms = Fade timeout), preventing
	// the flicker where new content briefly appears before the animation starts.
	const [displayedStep, setDisplayedStep] = useState(0)
	const displayedCurrent = tourSteps[displayedStep]
	const isDisplayedField = displayedCurrent.type === 'field'

	// Find next valid step, skipping steps whose skipIf returns true
	const resolveStep = useCallback((from, dir = 1) => {
		let s = from + dir
		while (s >= 0 && s < totalSteps) {
			if (!tourSteps[s].skipIf?.()) return s
			s += dir
		}
		return null
	}, [tourSteps, totalSteps])

	// Cleanup polling and step resources on unmount
	useEffect(() => {
		return () => {
			if (pollRef.current) clearInterval(pollRef.current)
			tourSteps.forEach(s => s.cleanup?.())
		}
	}, [])

	useEffect(() => {
		// Auto-skip step if condition met
		if (current.skipIf?.()) {
			const next = resolveStep(step)
			next !== null ? setStep(next) : onClose()
			return
		}

		setShowTooltip(false)
		setIsClicking(false)
		setWaiting(false)

		// Update displayed content only after fade-out completes (300ms = Fade timeout)
		// so the tooltip never shows the next step's content while still fading out.
		const FADE_OUT_MS = 200
		const tDisplay = setTimeout(() => setDisplayedStep(step), FADE_OUT_MS)

		if (!isField) {
			setTargetRect(null)
			const t = setTimeout(() => setShowTooltip(true), FADE_OUT_MS)
			return () => { clearTimeout(tDisplay); clearTimeout(t) }
		}

		// The target may not be there yet (a drawer or dialog the previous step opened is still mounting):
		// look for it for a while; once found, follow it until it has settled (a smooth scroll that starts late,
		// a panel sliding in, a table that loads and pushes the page) and on every later scroll
		const rectOf = el => { const r = el.getBoundingClientRect(); return { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom } }
		const sameRect = (a, b) => a && b && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5
		const startedAt = Date.now()
		let el = null, foundAt = 0, shown = null, frame = null
		setTargetRect(null)

		const follow = () => {
			// A table that refreshes (a running report's status) replaces its rows: look the target up again
			const gone = el.isConnected === false || (el.getBoundingClientRect().width === 0 && el.getBoundingClientRect().height === 0)
			if (gone) el = current.findTarget?.() || el
			const r = rectOf(el)
			if (r.width === 0 && r.height === 0) return
			if (!sameRect(r, shown)) { shown = r; setTargetRect(r) }
		}
		const onScroll = () => {
			if (!el || frame) return
			frame = requestAnimationFrame(() => { frame = null; follow() })
		}
		const tick = setInterval(() => {
			if (!el) {
				el = current.findTarget?.() || null
				if (!el) {
					if (Date.now() - startedAt > 2500) clearInterval(tick)
					return
				}
				foundAt = Date.now()
				// A target the card cannot sit beside (wide, like a full table or chart) starts at the top of the
				// screen, below the page header, so the card has room under it instead of covering it
				// (a step may return a group of elements as one box: it has no style of its own)
				const tall = el.getBoundingClientRect().width + CARD_WIDTH + 40 > window.innerWidth
				if (tall && el.style) el.style.scrollMarginTop = '96px'
				el.scrollIntoView?.({ behavior: 'smooth', block: tall ? 'start' : 'center' })
				el.__tourBlock = tall ? 'start' : 'center'
				return
			}
			// Chrome sometimes starts a smooth scroll seconds late, or not at all: if the target is still out of
			// view, jump to it
			if (!el.__tourJumped && Date.now() - foundAt > 450) {
				const r = el.getBoundingClientRect()
				if (r.bottom < 0 || r.top > window.innerHeight - 40 || r.right < 0 || r.left > window.innerWidth - 40 || r.top < 0) {
					el.scrollIntoView?.({ behavior: 'auto', block: el.__tourBlock || 'center' })
				}
				el.__tourJumped = true
			}
			follow()
			if (Date.now() - foundAt > 3000) clearInterval(tick)
		}, 80)
		window.addEventListener('scroll', onScroll, true)

		const t1 = setTimeout(() => setIsClicking(true), CURSOR_MS + 250)
		const t2 = setTimeout(() => { setIsClicking(false); setShowTooltip(true) }, TOOLTIP_DELAY_MS + 250)
		return () => {
			clearTimeout(tDisplay); clearInterval(tick); clearTimeout(t1); clearTimeout(t2)
			window.removeEventListener('scroll', onScroll, true)
			if (frame) cancelAnimationFrame(frame)
		}
	}, [step])

	useEffect(() => {
		const onResize = () => {
			if (isField && current.findTarget) {
				const el = current.findTarget()
				if (el) {
					const r = el.getBoundingClientRect()
					setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom })
				}
			}
		}
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [step])

	const handleNext = useCallback(() => {
		if (waiting) return

		if (current.action) {
			setWaiting(true)
			Promise.resolve(current.action()).then(() => {
				if (current.waitFor) {
					const pollId = setInterval(() => {
						if (current.waitFor()) {
							clearInterval(pollId)
							pollRef.current = null
							setWaiting(false)
							const next = resolveStep(step)
							next !== null ? setStep(next) : onClose()
						}
					}, 500)
					pollRef.current = pollId
					setTimeout(() => {
						if (pollRef.current === pollId) {
							clearInterval(pollId)
							pollRef.current = null
							setWaiting(false)
							const next = resolveStep(step)
							next !== null ? setStep(next) : onClose()
						}
					}, 30000)
				} else {
					setWaiting(false)
					const next = resolveStep(step)
					next !== null ? setStep(next) : onClose()
				}
			})
			return
		}

		const next = resolveStep(step)
		next !== null ? setStep(next) : onClose()
	}, [step, totalSteps, onClose, current, waiting, resolveStep])

	const handleBack = useCallback(() => {
		if (waiting) return
		const prev = resolveStep(step, -1)
		if (prev !== null) {
			for (let i = step; i > prev; i--) {
				tourSteps[i]?.reverseAction?.()
			}
			const targetRange = modalRanges.find(r => prev >= r.from && prev <= r.to)
			const currentRange = modalRanges.find(r => step >= r.from && step <= r.to)
			if (targetRange && targetRange !== currentRange) {
				if (targetRange.isOpen()) {
					setStep(prev)
				} else {
					setWaiting(true)
					Promise.resolve(targetRange.open()).then(() => {
						const pollId = setInterval(() => {
							if (targetRange.isOpen()) {
								clearInterval(pollId)
								setWaiting(false)
								setStep(prev)
							}
						}, 300)
						setTimeout(() => { clearInterval(pollId); setWaiting(false); setStep(prev) }, 8000)
					})
				}
			} else if (!targetRange && currentRange && currentRange.isOpen()) {
				currentRange.close()
				setStep(prev)
			} else {
				setStep(prev)
			}
		}
	}, [step, waiting, resolveStep, tourSteps, modalRanges])

	const handleDotClick = useCallback((i) => {
		if (waiting || i === step) return
		if (i < step) {
			for (let j = step; j > i; j--) {
				tourSteps[j]?.reverseAction?.()
			}
		}
		const targetRange = modalRanges.find(r => i >= r.from && i <= r.to)
		const currentRange = modalRanges.find(r => step >= r.from && step <= r.to)
		if (targetRange && targetRange !== currentRange) {
			if (targetRange.isOpen()) {
				setStep(i)
			} else {
				setWaiting(true)
				Promise.resolve(targetRange.open()).then(() => {
					const pollId = setInterval(() => {
						if (targetRange.isOpen()) {
							clearInterval(pollId)
							setWaiting(false)
							setStep(i)
						}
					}, 300)
					setTimeout(() => { clearInterval(pollId); setWaiting(false); setStep(i) }, 8000)
				})
			}
		} else if (!targetRange && currentRange && currentRange.isOpen()) {
			currentRange.close()
			setStep(i)
		} else {
			setStep(i)
		}
	}, [step, waiting, tourSteps, modalRanges])

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (waiting) return

			switch (e.key) {
				case 'ArrowRight':
				case 'Enter':
					e.preventDefault()
					handleNext()
					break
				case 'ArrowLeft':
					e.preventDefault()
					handleBack()
					break
				case 'Escape':
					e.preventDefault()
					onClose()
					break
				default:
					break
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [waiting, handleNext, handleBack, onClose])

	const rect = targetRect
	const cursorX = rect ? rect.left + rect.width * 0.45 : -50
	// Place the card with its real height: steps with an example are taller than the 300px default
	useLayoutEffect(() => {
		if (cardRef.current) setCardHeight(cardRef.current.offsetHeight)
	}, [displayedStep])

	const { clipBottom, ...tooltipPos } = getTooltipPosition(rect, cardHeight)
	const isCentered = !isDisplayedField || !rect
	const spotHeight = rect ? (clipBottom ? Math.max(40, Math.min(rect.bottom + 10, clipBottom) - (rect.top - 10)) : rect.height + 20) : 0
	// the cursor points inside the part of the target that is lit
	const cursorY = rect ? rect.top + Math.min(rect.height, spotHeight - 20) * 0.55 : -50

	return <>
		{/* Click blocker: a click outside the card closes the tour */}
		<Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 9997 }} />

		{/* Spotlight or backdrop */}
		{isDisplayedField && rect
			? <Box sx={{
				position: 'fixed',
				top: rect.top - 10,
				left: rect.left - 10,
				width: rect.width + 20,
				height: spotHeight,
				borderRadius: '12px',
				boxShadow: `0 0 0 9999px ${BACKDROP}, 0 0 0 3px ${alpha(dsColorNew.cyan, 0.55)}, 0 0 28px 6px ${alpha(dsColorNew.webBlue500, 0.45)}`,
				zIndex: 9998,
				pointerEvents: 'none',
				transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
				willChange: 'top, left, width, height',
			}} />
			: <Box onClick={onClose} sx={{
				position: 'fixed', inset: 0,
				backgroundColor: BACKDROP,
				backdropFilter: 'blur(2px)',
				zIndex: 9998,
				transition: 'opacity 0.4s',
			}} />
		}

		{/* Animated cursor */}
		{isDisplayedField && rect && <Box sx={{
			position: 'fixed',
			left: cursorX,
			top: cursorY,
			zIndex: 10002,
			pointerEvents: 'none',
			transition: `left ${CURSOR_MS}ms cubic-bezier(0.4, 0, 0.2, 1), top ${CURSOR_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
			transform: isClicking ? 'scale(0.78) rotate(-8deg)' : 'scale(1) rotate(0deg)',
			transformOrigin: '2px 2px',
			willChange: 'left, top, transform',
		}}>
			<CursorSVG />
		</Box>}

		{/* Tooltip card */}
		<Fade in={showTooltip} timeout={200}>
			<Paper ref={cardRef} elevation={0} sx={{
				position: 'fixed',
				...(isCentered
					? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
					: tooltipPos
				),
				width: isCentered ? 460 : CARD_WIDTH,
				zIndex: 10001,
				borderRadius: '16px',
				overflow: 'hidden',
				pointerEvents: 'all',
				backgroundColor: dsColorNew.white,
				border: `1px solid ${dsColorNew.grayBorder}`,
				boxShadow: `0 24px 60px ${alpha(dsColorNew.navy, 0.35)}`,
			}}>
				{/* Progress bar + close button */}
				<Box sx={{ position: 'relative' }}>
					<Box sx={{ height: 4, backgroundColor: dsColorNew.grayBorder }}>
						<Box sx={{
							height: 1,
							width: `${((displayedStep + 1) / totalSteps) * 100}%`,
							background: BRAND_GRADIENT,
							transition: 'width 0.4s ease',
						}} />
					</Box>
					<Button
						size="small"
						onClick={onClose}
						sx={{
							position: 'absolute', top: 8, right: 8,
							minWidth: 28, width: 28, height: 28, p: 0,
							borderRadius: '50%',
							color: dsColorNew.textMuted,
							backgroundColor: dsColorNew.filterInputBg,
							'&:hover': { backgroundColor: dsColorNew.hoverBgLight, color: dsColorNew.webBlue500 },
						}}
					>
						<IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
					</Button>
				</Box>

				<Box sx={{ p: 3, pt: 2 }}>
					{/* Step counter */}
					<Typography variant="caption" sx={{
						color: dsColorNew.webBlue500, fontWeight: 700, letterSpacing: 1.2, fontSize: '0.68rem',
					}}>
						STEP {displayedStep + 1} / {totalSteps}
					</Typography>

					{/* Icon + Title */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, mb: 1.5 }}>
						<Box sx={{
							width: isCentered ? 52 : 40,
							height: isCentered ? 52 : 40,
							borderRadius: '12px',
							background: BRAND_GRADIENT,
							boxShadow: `0 6px 16px ${alpha(dsColorNew.webBlue500, 0.3)}`,
							display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
						}}>
							<IconSeeker icon={displayedCurrent.icon} sx={{ fontSize: isCentered ? 22 : 17, color: dsColorNew.white }} />
						</Box>
						<Typography variant={isCentered ? 'h5' : 'h6'} sx={{ fontWeight: 700, color: dsColorNew.textTitle, lineHeight: 1.25 }}>
							{displayedCurrent.title}
						</Typography>
					</Box>

					{/* Description */}
					<Typography variant="body2" sx={{
						color: dsColorNew.textMuted, lineHeight: 1.75, mb: displayedCurrent.example ? 2 : 0,
						fontSize: isCentered ? '0.9rem' : '0.84rem',
					}}>
						{displayedCurrent.description}
					</Typography>

					{/* Example chip */}
					{displayedCurrent.example && <Box sx={{
						backgroundColor: dsColorNew.hoverBgLight,
						borderRadius: '8px',
						px: 2, py: 1,
						border: `1px dashed ${alpha(dsColorNew.webBlue500, 0.4)}`,
					}}>
						<Typography variant="caption" sx={{ color: dsColorNew.webBlueHover, fontWeight: 600 }}>
							{displayedCurrent.example}
						</Typography>
					</Box>}

					{/* Navigation */}
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
						<Button
							size="small"
							disabled={step === 0 || waiting}
							onClick={handleBack}
							sx={{
								textTransform: 'none', color: dsColorNew.textDark, minWidth: 60, borderRadius: '8px', fontWeight: 600,
								backgroundColor: dsColorNew.white,
								border: `1px solid ${dsColorNew.grayBorder}`,
								'&:hover': { backgroundColor: dsColorNew.hoverBgLight, borderColor: dsColorNew.webBlue500, color: dsColorNew.webBlue500 },
								'&.Mui-disabled': { backgroundColor: dsColorNew.white, color: alpha(dsColorNew.textMuted, 0.5), borderColor: dsColorNew.grayBorder },
							}}
						>
							Back
						</Button>

						{/* Dots */}
						<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 160 }}>
							{tourSteps.map((_, i) => (
								<Box key={i} onClick={() => handleDotClick(i)} sx={{
									width: i === step ? 18 : 6,
									height: 6,
									borderRadius: '3px',
									backgroundColor: i === step ? dsColorNew.webBlue500 : i < step ? alpha(dsColorNew.appBlue, 0.55) : dsColorNew.grayBorder,
									transition: 'all 0.3s ease',
									cursor: i !== step ? 'pointer' : 'default',
									'&:hover': i !== step ? { opacity: 0.7 } : {},
								}} />
							))}
						</Box>

						<Button
							variant="contained"
							size="small"
							color="primary"
							onClick={handleNext}
							disabled={waiting}
							disableElevation
							sx={{
								textTransform: 'none', px: 1.5, borderRadius: '8px', minWidth: 72, fontWeight: 600,
								color: dsColorNew.white, background: BRAND_GRADIENT,
								'&:hover': { background: BRAND_GRADIENT_HOVER },
							}}
						>
							{waiting ? 'Loading...' : step === totalSteps - 1 ? 'Finish' : 'Next'}
						</Button>
					</Box>

					{/* Keyboard hint */}
					<Typography sx={{ mt: 2, fontSize: 11, color: dsColorNew.textMuted, textAlign: 'center', opacity: 0.85 }}>
						← → to navigate · Esc or click outside to close
					</Typography>
				</Box>
			</Paper>
		</Fade>
	</>
}


// Pure presentational button — no hooks (avoids React queue bug in actions slot)
export default function VirtualTourButton({ onClick }) {
	return <Box>
		<Button
			variant="outlined"
			size="small"
			startIcon={<IconSeeker icon="circle-info" />}
			onClick={onClick}
			sx={{
				textTransform: 'none', borderRadius: '8px', px: 2, fontWeight: 600,
				color: dsColorNew.webBlue500, backgroundColor: dsColorNew.white, borderColor: dsColorNew.grayBorder,
				'&:hover': { backgroundColor: dsColorNew.hoverBgLight, borderColor: dsColorNew.webBlue500 },
			}}
		>
			Virtual Tour
		</Button>
	</Box>
}
